import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Task } from '../../../../types/task';
import { TaskItemV2 } from './TaskItemV2';
import { handleTimelineDrag, handleTimelineResize, canDragTask } from '../../../../utils/taskDragUtils';

interface DraggableTimelineTaskProps {
  task: Task;
  onToggle: (taskId: string, completed: boolean) => void;
  onClick: (taskId: string) => void;
  onTaskUpdate: (updateData: any) => Promise<boolean>;
  timelineHeight: number;
  startHour: number;
  endHour: number;
  pixelsPerHour: number;
}

type DragMode = 'none' | 'move' | 'resize-top' | 'resize-bottom';

/**
 * 可拖拽的时间轴任务项组件
 * 支持整体拖拽移动和顶部/底部调整大小
 */
export const DraggableTimelineTask: React.FC<DraggableTimelineTaskProps> = ({
  task,
  onToggle,
  onClick,
  onTaskUpdate,
  timelineHeight,
  startHour,
  endHour,
  pixelsPerHour
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragMode, setDragMode] = useState<DragMode>('none');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [originalPosition, setOriginalPosition] = useState(0);

  // 标记是否进行了实际的拖拽操作，用于区分拖拽和点击
  const [hasDragged, setHasDragged] = useState(false);

  // 移动端触摸跟踪
  const touchStartRef = useRef<{ x: number; y: number; mode: DragMode } | null>(null);

  // 计算任务的当前位置信息
  const taskInfo = React.useMemo(() => {
    if (!task.startAt) return null;
    
    const taskStartTime = new Date(task.startAt);
    const taskStartHour = taskStartTime.getHours() + taskStartTime.getMinutes() / 60;
    const taskDuration = task.cost ? task.cost / 3600 : 1; // 默认1小时
    const taskEndHour = taskStartHour + taskDuration;
    
    return {
      startHour: taskStartHour,
      endHour: taskEndHour,
      duration: taskDuration
    };
  }, [task.startAt, task.cost]);

  // 处理鼠标按下事件
  const handleMouseDown = useCallback((e: React.MouseEvent, mode: DragMode) => {
    console.log('鼠标按下事件触发:', {
      mode,
      taskId: task.id,
      taskTitle: task.title,
      taskStartAt: task.startAt,
      taskDone: task.done,
      canDrag: canDragTask(task, mode === 'move' ? 'move' : 'resize'),
      hasTaskInfo: !!taskInfo,
      taskInfo
    });

    if (!canDragTask(task, mode === 'move' ? 'move' : 'resize') || !taskInfo) {
      console.log('拖拽被阻止:', {
        canDrag: canDragTask(task, mode === 'move' ? 'move' : 'resize'),
        hasTaskInfo: !!taskInfo,
        taskStartAt: task.startAt,
        taskDone: task.done
      });
      return;
    }

    console.log('开始拖拽:', mode, taskInfo);
    e.preventDefault();
    e.stopPropagation();

    // 重置拖拽标记
    setHasDragged(false);

    setDragMode(mode);
    setIsDragging(true);
    setDragStartY(e.clientY);
    setOriginalPosition(taskInfo.startHour);
  }, [task, taskInfo]);

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !taskInfo || dragMode === 'none') return;

    console.log('拖拽移动:', dragMode, e.clientY - dragStartY);
    const deltaY = e.clientY - dragStartY;
    const deltaHours = deltaY / pixelsPerHour;

    // 一旦检测到鼠标移动，就标记为已拖拽
    if (Math.abs(deltaY) > 0) {
      setHasDragged(true);
    }

    if (dragMode === 'move') {
      // 整体移动
      const newStartHour = Math.max(startHour, Math.min(endHour - taskInfo.duration, originalPosition + deltaHours));

      // 更新视觉位置
      if (containerRef.current) {
        const newTopPosition = (newStartHour - startHour) * pixelsPerHour;
        containerRef.current.style.transform = `translateY(${newTopPosition - (originalPosition - startHour) * pixelsPerHour}px)`;
      }
    } else if (dragMode === 'resize-top') {
      // 调整顶部（改变开始时间）
      const newStartHour = Math.max(startHour, Math.min(taskInfo.endHour - 0.25, originalPosition + deltaHours)); // 最小15分钟

      if (containerRef.current) {
        const newTopPosition = (newStartHour - startHour) * pixelsPerHour;
        const newHeight = (taskInfo.endHour - newStartHour) * pixelsPerHour;
        containerRef.current.style.transform = `translateY(${newTopPosition - (originalPosition - startHour) * pixelsPerHour}px)`;
        containerRef.current.style.height = `${Math.max(newHeight, 44)}px`;
      }
    } else if (dragMode === 'resize-bottom') {
      // 调整底部（改变结束时间）
      const newEndHour = Math.max(taskInfo.startHour + 0.25, Math.min(endHour, taskInfo.endHour + deltaHours)); // 最小15分钟

      if (containerRef.current) {
        const newHeight = (newEndHour - taskInfo.startHour) * pixelsPerHour;
        containerRef.current.style.height = `${Math.max(newHeight, 44)}px`;
      }
    }
  }, [isDragging, taskInfo, dragMode, dragStartY, originalPosition, startHour, endHour, pixelsPerHour]);

  // 处理鼠标释放事件
  const handleMouseUp = useCallback(async (e: MouseEvent) => {
    if (!isDragging || !taskInfo || dragMode === 'none') return;

    console.log('拖拽结束:', dragMode);
    const deltaY = e.clientY - dragStartY;
    const deltaHours = deltaY / pixelsPerHour;

    let result;

    if (dragMode === 'move') {
      const newStartHour = Math.max(startHour, Math.min(endHour - taskInfo.duration, originalPosition + deltaHours));
      result = handleTimelineDrag(task, newStartHour);
    } else if (dragMode === 'resize-top') {
      const newStartHour = Math.max(startHour, Math.min(taskInfo.endHour - 0.25, originalPosition + deltaHours));
      result = handleTimelineResize(task, newStartHour, taskInfo.endHour);
    } else if (dragMode === 'resize-bottom') {
      const newEndHour = Math.max(taskInfo.startHour + 0.25, Math.min(endHour, taskInfo.endHour + deltaHours));
      result = handleTimelineResize(task, taskInfo.startHour, newEndHour);
    }

    // 重置拖拽状态
    setIsDragging(false);
    setDragMode('none');

    // 重置视觉样式
    if (containerRef.current) {
      containerRef.current.style.transform = '';
      containerRef.current.style.height = '';
    }

    // 提交更新
    if (result && !result.shouldRevert) {
      const success = await onTaskUpdate(result.updateData);
      if (!success) {
        console.warn('任务更新失败');
      }
    }
  }, [isDragging, taskInfo, dragMode, dragStartY, originalPosition, startHour, endHour, pixelsPerHour, task, onTaskUpdate]);

  // 处理触摸移动事件（移动端）
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !taskInfo || dragMode === 'none') return;

    // 在拖拽移动时阻止默认行为（如滚动）
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;

    console.log('触摸移动:', touch.clientY);
    // 直接调用鼠标移动处理逻辑
    const mockMouseEvent = {
      clientY: touch.clientY
    } as MouseEvent;
    handleMouseMove(mockMouseEvent);
  }, [isDragging, taskInfo, dragMode, handleMouseMove]);

  // 处理触摸结束事件（移动端）
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!isDragging || !taskInfo || dragMode === 'none') return;

    e.preventDefault();
    const touch = e.changedTouches[0];
    if (!touch) return;

    console.log('触摸结束:', touch.clientY);
    // 直接调用鼠标释放处理逻辑
    const mockMouseEvent = {
      clientY: touch.clientY
    } as MouseEvent;
    handleMouseUp(mockMouseEvent);
  }, [isDragging, taskInfo, dragMode, handleMouseUp]);

  // 管理拖拽事件监听器
  useEffect(() => {
    if (isDragging) {
      console.log('添加拖拽事件监听器');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // 添加触摸事件支持（移动端）
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  if (!taskInfo) {
    return (
      <TaskItemV2
        task={task}
        onToggle={onToggle}
        onClick={onClick}
        viewMode="timeline"
        timelineHeight={timelineHeight}
        hasDragged={false}
      />
    );
  }

  const canMove = canDragTask(task, 'move');
  const canResize = canDragTask(task, 'resize');

  return (
    <div
      ref={containerRef}
      className={`relative group ${isDragging ? 'z-50' : ''}`}
      style={{
        cursor: isDragging ? 'grabbing' : (canMove ? 'grab' : 'default')
      }}
    >
      {/* 顶部调整大小手柄 */}
      {canResize && (
        <div
          className="absolute top-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 dark:hover:bg-ios-dark-gray4 rounded-t flex items-center justify-center"
          onMouseDown={(e) => handleMouseDown(e, 'resize-top')}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            if (touch) {
              const mouseEvent = {
                clientY: touch.clientY,
                preventDefault: () => e.preventDefault(),
                stopPropagation: () => e.stopPropagation()
              } as React.MouseEvent;
              handleMouseDown(mouseEvent, 'resize-top');
            }
          }}
          title="拖拽调整开始时间"
        >
          <div className="w-8 h-0.5 bg-gray-400 rounded-full"></div>
        </div>
      )}

      {/* 任务内容 */}
      <div
        onMouseDown={canMove ? (e) => handleMouseDown(e, 'move') : undefined}
        onTouchStart={canMove ? (e) => {
          const touch = e.touches[0];
          if (touch) {
            // 记录触摸开始位置，但不立即进入拖拽模式
            touchStartRef.current = {
              x: touch.clientX,
              y: touch.clientY,
              mode: 'move'
            };
          }
        } : undefined}
        onTouchMove={canMove ? (e) => {
          if (!touchStartRef.current || isDragging) return;

          const touch = e.touches[0];
          if (touch) {
            const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
            const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            // 移动距离超过5px时进入拖拽模式
            if (distance > 5) {
              e.preventDefault(); // 阻止滚动等默认行为

              // 创建模拟的鼠标事件并进入拖拽模式
              const mouseEvent = {
                clientY: touchStartRef.current.y,
                preventDefault: () => {},
                stopPropagation: () => {}
              } as React.MouseEvent;

              handleMouseDown(mouseEvent, touchStartRef.current.mode);
            }
          }
        } : undefined}
        onTouchEnd={canMove ? () => {
          // 清理触摸跟踪状态
          touchStartRef.current = null;
        } : undefined}
        className={`${canMove ? 'cursor-grab active:cursor-grabbing' : ''}`}
        style={{
          cursor: isDragging ? 'grabbing' : (canMove ? 'grab' : undefined)
        }}
      >
        <TaskItemV2
          task={task}
          onToggle={onToggle}
          onClick={onClick}
          viewMode="timeline"
          timelineHeight={timelineHeight}
          hasDragged={hasDragged}
        />
      </div>

      {/* 底部调整大小手柄 */}
      {canResize && (
        <div
          className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 dark:hover:bg-ios-dark-gray4 rounded-b flex items-center justify-center"
          onMouseDown={(e) => handleMouseDown(e, 'resize-bottom')}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            if (touch) {
              const mouseEvent = {
                clientY: touch.clientY,
                preventDefault: () => e.preventDefault(),
                stopPropagation: () => e.stopPropagation()
              } as React.MouseEvent;
              handleMouseDown(mouseEvent, 'resize-bottom');
            }
          }}
          title="拖拽调整结束时间"
        >
          <div className="w-8 h-0.5 bg-gray-400 rounded-full"></div>
        </div>
      )}
    </div>
  );
};
